import React from 'react';
import { cn } from '../lib/utils';
import {
  controlFrameClassName,
  controlSizeClassName,
  type ControlSize,
  type ControlDensity,
} from './control-styles';

export type FieldDensity = ControlDensity;
export type FieldControlSize = ControlSize;

export const fieldControlClassName = `${controlFrameClassName} flex w-full border-border hover:border-control-border bg-surface-sheet text-text-heading transition-interactive placeholder:text-text-muted focus:border-focus focus:outline-none focus:ring-2 focus:ring-focus focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-status-danger-fg aria-[invalid=true]:focus:border-status-danger-fg motion-reduce:transition-none`;

export function fieldDensityClassName(
  density: FieldDensity = 'productive',
  size: FieldControlSize = 'default',
) {
  return controlSizeClassName(size, density);
}

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  error?: boolean;
  /** @deprecated Use `size="compact"` for new inline-editor controls. */
  compact?: boolean;
  density?: FieldDensity;
  size?: FieldControlSize;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, error, compact, density, size, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(
          fieldControlClassName,
          fieldDensityClassName(density, size ?? (compact ? 'compact' : 'default')),
          className,
          // AFTER `className`: tailwind-merge is last-wins, so a caller passing
          // its own border utility silently discarded the error state and an
          // invalid field looked identical to a valid one.
          error && 'border-status-danger-fg focus:border-status-danger-fg focus:ring-focus',
        )}
        ref={ref}
        {...props}
        aria-invalid={error || props['aria-invalid']}
        aria-required={props.required || props['aria-required']}
      />
    );
  },
);
Input.displayName = 'Input';

/**
 * Shared field-appearance tokens for NATIVE `<select>` / `<input>` controls that
 * must keep the native element (e.g. a select inside a server-action form
 * server-action where FormData submission is required and the JS `Select`
 * component — which emits no native `name` — would break it). Mirrors the
 * default (non-compact) `Input` look so native controls match platform fields.
 * Apply alongside any layout classes the caller already needs (margins, grid).
 */
export const nativeFieldClassName = cn(fieldControlClassName, fieldDensityClassName());

export { Input };
